import { useState } from 'react';

import { observabilityEnvironmentLogs } from '@api/sdk.gen';

import { EnvironmentId } from '@/react/portainer/environments/types';

import { InformationPanel } from '@@/InformationPanel';
import { Alert } from '@@/Alert';
import { InlineLoader } from '@@/InlineLoader';
import { DetailLogsViewer } from '@@/DetailLogsViewer/DetailLogsViewer';
import { DetailLogsQueryParams, LogEntry } from '@@/DetailLogsViewer/types';

import { queryKeys } from '../../queries/queryKeys';
import { useEnvironmentObservability } from '../../queries/useEnvironmentObservability';

import { buildOneUptimeUrl } from './utils';

interface Props {
  environmentId: EnvironmentId;
  namespace?: string;
  /** Workload or pod kind to scope logs to, e.g. Deployment, StatefulSet, DaemonSet, Pod. Must be paired with `name`. */
  kind?: string;
  /** Workload or pod name to scope logs to. Must be paired with `kind`. */
  name?: string;
}

const ONE_HOUR_S = 60 * 60;

// Wrapper class for views that render the logs viewer as a page-level tab.
// Full height minus 148px = 48px navbar + 60px page header + 40px tab bar.
// mx-4 matches the horizontal inset of the other tabs' widgets so the logs
// viewer aligns with its sibling tabs.
export const LOGS_TAB_WRAPPER_CLASS = 'mx-4 h-[calc(100vh-148px)]';

export function ObservabilityLogs({
  environmentId,
  namespace = '',
  kind,
  name,
}: Props) {
  const obsQuery = useEnvironmentObservability(environmentId);
  const [defaultUntil] = useState(() => Math.floor(Date.now() / 1000));
  const defaultSince = defaultUntil - ONE_HOUR_S;
  const oneUptimeUrl = obsQuery.data
    ? buildOneUptimeUrl(obsQuery.data, namespace, kind, name)
    : undefined;

  if (obsQuery.isLoading) {
    return <InlineLoader>Checking observability status...</InlineLoader>;
  }

  if (obsQuery.isError) {
    return (
      <Alert color="error" title="Failed to load observability status">
        Unable to determine if observability is enabled for this environment.
      </Alert>
    );
  }

  if (!obsQuery.data?.enabled) {
    return (
      <InformationPanel>
        Observability is not configured for this environment. An administrator
        can enable it by assigning an Observability policy to this
        environment&apos;s group.
      </InformationPanel>
    );
  }

  return (
    <DetailLogsViewer
      queryKey={queryKeys.observabilityLogs(
        environmentId,
        namespace,
        kind,
        name
      )}
      getLogsFn={(params) =>
        getObservabilityLogs(environmentId, namespace, kind, name, params)
      }
      projectUrl={oneUptimeUrl}
      defaultSince={defaultSince}
      defaultUntil={defaultUntil}
    />
  );
}

async function getObservabilityLogs(
  environmentId: EnvironmentId,
  namespace: string,
  kind: string | undefined,
  name: string | undefined,
  { since, until, limit, skip, severities, search }: DetailLogsQueryParams
): Promise<{ logs: LogEntry[] }> {
  const { data } = await observabilityEnvironmentLogs({
    path: { id: environmentId },
    query: {
      namespace: namespace || undefined,
      kind: kind && name ? kind : undefined,
      name: kind && name ? name : undefined,
      search: search || undefined,
      severity: severities.length > 0 ? severities : undefined,
      from: new Date(since * 1000).toISOString(),
      to: new Date(until * 1000).toISOString(),
      skip: skip > 0 ? skip : undefined,
      limit: limit > 0 ? limit : undefined,
    },
  });

  return {
    logs: (data.logs ?? []).map((entry) => ({
      time: entry.time ?? '',
      severity: entry.severity ?? '',
      message: entry.message ?? '',
      source: entry.source ?? '',
      labels: entry.labels,
    })),
  };
}
